/**
 * Подписать документ заявки через SIMA İmza вместо загрузки скана — шаг «Подписанный
 * экземпляр» договора (ТЗ-2026-015).
 *
 * Порядок такой: выбрать подписанта (сотрудник с подтверждённым сертификатом) → отправить
 * вложение заявки на подпись → показать QR, его сканируют встроенным сканером приложения →
 * дождаться результата. Подписанный файл НЕ прикладывается к заявке сам: его `uploadId`
 * уходит в решение шага как обычный скан, иначе «Согласовать» упало бы с «файл уже приложен».
 *
 * Выбор подписантов ограничен теми, чей сертификат нам известен: операция на незнакомый
 * сертификат никогда не закроется — SIMA сверяет фильтр `p_<serialNumber>` на телефоне.
 *
 * Второй путь — КОНТРАГЕНТ: ему выдаётся ссылка на публичную страницу `/s/<токен>`, учётной
 * записи в портале у него нет и быть не должно. Подписанта там называет сервер — по ВÖİN из
 * самой заявки, фронт его не передаёт.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Text, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { signingEnabled } from '../../../app/nav'
import { useApp } from '../../../i18n'
import { SigningOperationCard } from '../../signing/components/SigningOperationCard'
import { useSigningOperation } from '../../signing/useSigning'
import type { EdoAttachment } from '../../../lib/types'
import { tone } from '../../../theme/tones'
import { Button, OptionSelect } from '../../../ui'
import { errorText } from '../errorText'
import { useCopyLink } from '../useCopyLink'

export function SignWithSima({
  attachments,
  onSigned,
}: {
  attachments?: EdoAttachment[]
  /** Подписанный файл готов: его uploadId уходит в решение шага вместо скана. */
  onSigned: (signed: { uploadId: string; name: string }) => void
}) {
  const { t } = useApp()
  const [open, setOpen] = useState(false)
  const [signer, setSigner] = useState<string[]>([])
  const [docId, setDocId] = useState<string[]>([])
  const [opId, setOpId] = useState<string | null>(null)

  // Подписывать можно только PDF: подпись врезается внутрь файла (PAdES). Последний файл
  // первым — после доработки подписывают новую редакцию, а не исходный документ.
  const pdfs = (attachments ?? []).filter((a) => a.upload.mime === 'application/pdf').reverse()
  const signers = useQuery({
    queryKey: ['signing-signers'],
    queryFn: () => api.signingSigners(),
    enabled: open,
  })
  const doc = pdfs.find((a) => a.uploadId === docId[0]) ?? pdfs[0]
  const send = useMutation({
    mutationFn: () => api.signAttachment(doc.uploadId, signer[0]),
    onSuccess: (op) => setOpId(op.id),
  })
  // Ссылка контрагенту: операция та же, только подписывают её по публичной странице. Номер
  // операции кладём туда же (`opId`) — автор по нему видит, что подпись поставлена, и
  // подписанный файл так же уходит в решение шага.
  const link = useMutation({
    mutationFn: () => api.signAttachmentLink(doc.uploadId),
    onSuccess: (op) => setOpId(op.id),
  })
  const { copy, copied } = useCopyLink(() => link.data?.publicUrl ?? '')
  // Пока документ подписывают на телефоне, статус меняется не у нас — опрашиваем операцию.
  const op = useSigningOperation(opId)
  const live = op.data ?? send.data

  const signed = op.data?.status === 'completed' ? op.data.resultUploadId : null
  useEffect(() => {
    if (signed) onSigned({ uploadId: signed, name: doc?.upload.name ?? 'signed.pdf' })
    // Только по самому файлу: onSigned и doc — новые объекты на каждом рендере родителя,
    // в зависимостях они дали бы повторный вызов на каждый рендер.
  }, [signed])

  // Ключей SIMA ещё нет — кнопка только расстроила бы: ручка ответит 503.
  if (!signingEnabled() || !pdfs.length) return null

  if (!open) {
    return (
      <Button size="sm" onPress={() => setOpen(true)}>
        {t({ ru: 'Подписать через SIMA İmza', en: 'Sign with SIMA İmza', az: 'SIMA İmza ilə imzala' })}
      </Button>
    )
  }

  const list = signers.data ?? []

  return (
    <YStack gap={8}>
      {!opId && !link.data ? (
        <YStack gap={6}>
          <Button
            size="sm"
            disabled={link.isPending}
            opacity={link.isPending ? 0.5 : 1}
            onPress={() => link.mutate()}
          >
            {t({
              ru: 'Выдать ссылку контрагенту',
              en: 'Issue a link to the counterparty',
              az: 'Qarşı tərəfə keçid ver',
            })}
          </Button>
          <Text fontSize={12} color="$muted">
            {t({
              ru: 'Контрагент подпишет по ссылке, без входа в портал: подписанта называем его ВÖİN из заявки.',
              en: 'The counterparty signs via the link, without a portal login: we address it by the VÖEN from the request.',
              az: 'Qarşı tərəf portala girmədən keçid ilə imzalayır: ünvan — müraciətdəki VÖEN.',
            })}
          </Text>
        </YStack>
      ) : null}

      {link.data ? (
        <YStack gap={6}>
          <Text fontSize={12} color="$muted">
            {t({
              ru: 'Отправьте ссылку контрагенту. Она действует до',
              en: 'Send the link to the counterparty. It is valid until',
              az: 'Keçidi qarşı tərəfə göndərin. Etibarlıdır:',
            })}{' '}
            {new Date(link.data.publicUrlExpiresAt).toLocaleString()}
          </Text>
          <Text fontSize={12} color="$color" selectable>
            {link.data.publicUrl}
          </Text>
          <Button size="sm" onPress={() => void copy()}>
            {copied
              ? t({ ru: 'Скопировано', en: 'Copied', az: 'Kopyalandı' })
              : t({ ru: 'Скопировать ссылку', en: 'Copy the link', az: 'Keçidi kopyala' })}
          </Button>
        </YStack>
      ) : null}

      {link.error instanceof Error ? (
        <Text fontSize={12} color={tone('red').text}>
          {t(errorText(link.error))}
        </Text>
      ) : null}

      {signers.isSuccess && list.length === 0 ? (
        <Text fontSize={12} color="$muted">
          {t({
            ru: 'Пока никто не подтвердил вход через SIMA İmza — адресовать документ некому. Подписант должен один раз подтвердить вход на странице «Подписание», после этого он появится в списке.',
            en: 'Nobody has confirmed a SIMA İmza login yet, so there is no one to address the document to. The signer confirms the login once on the Signing page and then appears in this list.',
            az: 'Hələ heç kim SIMA İmza ilə girişi təsdiqləməyib — sənədi ünvanlamağa adam yoxdur. İmzalayan «İmzalama» səhifəsində girişi bir dəfə təsdiqləməlidir, sonra siyahıda görünəcək.',
          })}
        </Text>
      ) : null}

      {list.length > 0 && !opId ? (
        <YStack gap={8}>
          {pdfs.length > 1 ? (
            <OptionSelect
              options={pdfs.map((a) => ({ value: a.uploadId, label: a.upload.name }))}
              value={docId.length ? docId : [pdfs[0].uploadId]}
              onChange={setDocId}
            />
          ) : (
            <Text fontSize={12} color="$muted">
              {t({ ru: 'Подпишем', en: 'To sign', az: 'İmzalanacaq' })}: {pdfs[0].upload.name}
            </Text>
          )}
          <OptionSelect
            options={list.map((u) => ({ value: u.id, label: u.fullName }))}
            value={signer}
            onChange={setSigner}
            placeholder={t({ ru: 'Кто подписывает', en: 'Who signs', az: 'Kim imzalayır' })}
            highlight={!signer.length}
          />
          <Button
            variant="primary"
            disabled={!signer.length || send.isPending}
            opacity={!signer.length || send.isPending ? 0.5 : 1}
            onPress={() => send.mutate()}
          >
            {send.isPending
              ? t({ ru: 'Отправляем…', en: 'Sending…', az: 'Göndərilir…' })
              : t({ ru: 'Отправить на подпись', en: 'Send for signing', az: 'İmzaya göndər' })}
          </Button>
        </YStack>
      ) : null}

      {live ? <SigningOperationCard op={live} ticket={!link.data} /> : null}

      {send.error instanceof Error ? (
        <Text fontSize={12} color={tone('red').text}>
          {t(errorText(send.error))}
        </Text>
      ) : null}
    </YStack>
  )
}
